import { IndexRoute, Route, Router, Redirect } from 'react-router'
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3'
import { applyMiddleware, createStore, Store } from 'redux'
// @ts-ignore
import thunk from 'redux-thunk'
import { Provider } from 'react-redux'
import { values } from 'lodash'

import { withStaticProps } from 'lib/decorators'
import * as blUrls from 'lib/urls'
import { getHistory } from 'lib/history'
import * as BLConstants from 'shared/constants'

import PostTeaserList from 'hello_baby/components/post-teaser-list'
import TopicView from 'hello_baby/components/topic-view'
import { withContextualizedTracking, track } from 'lib/analytics'
import {
  setSearchId,
  GUIDE_SEARCH_INDEX_KEY,
} from 'shared/hooks/useSearchId/useSearchId'
import { getTopicSlug } from '../lib'
import helloBabyReducer, { getPostBySlug } from '../reducers'
import { SET_COLLECTIONS, SET_POSTS } from '../actions'
import App from '../components/app'
import LandingPage from './landing-page'
import PostWrapper from './post-wrapper'
import PostsDataLoader from './posts-data-loader'
import PostList from './post-list'
import {
  GEAR_SLUG,
  PARENTING_SLUG,
  PREGNANCY_SLUG,
  PREGNANCY_WEEK_BY_WEEK_SLUG,
  SAMPLE_REGISTRIES_SLUG,
  FRIENDS_AND_FAMILY_SLUG,
} from '../constants'
import AdminTool from '../components/admin-tool'
import { FlagsProvider } from 'flagged'
import { useEffect, useState } from 'react'
import useCurrentUser from 'shared/hooks/useCurrentUser/useCurrentUser'
import { CompatRouter } from 'react-router-dom-v5-compat'

const middlewares = [thunk]

if (process.env.NODE_ENV === 'development') {
  // middlewares.push(createLogger())
}

const createStoreWithMiddleware = applyMiddleware(...middlewares)(createStore)

const scrollTo = (elId: string) => {
  const el = document.getElementById(elId)
  if (el) {
    document.body.scrollTop = el.offsetTop - 80 // 80px to account for sticky nav
  }
}

interface HelloBabyAppProps {
  collections: {
    'hello-baby-home-topics': object
    topicNavigation: object
  }
  emailUrls: [string]
  featureFlags: {}
  isEmailSubscriber: boolean
  path: string
  posts?: object
  searchId?: string
  searchPosts: []
  sectionId: string
  topics: [
    {
      slug: string
    }
  ]
}

const HelloBabyApp = ({
  collections,
  emailUrls,
  featureFlags,
  isEmailSubscriber,
  path,
  posts,
  searchId,
  searchPosts,
  sectionId,
  topics,
}: HelloBabyAppProps) => {
  const [currentUser] = useCurrentUser()

  const [store] = useState<Store<unknown>>(
    createStoreWithMiddleware(helloBabyReducer)
  )

  store.dispatch({
    type: SET_COLLECTIONS,
    collections,
  })

  if (posts) {
    store.dispatch({ type: SET_POSTS, posts })
  }

  const history = getHistory({ path, useRouter: true })

  const topicNavigation =
    collections[BLConstants.COLLECTION_SLUG_TOPIC_NAVIGATION]

  const homeTopics = collections[BLConstants.COLLECTION_SLUG_HOME_TOPICS]

  const DecoratedLandingPage = withStaticProps('LandingPage', {
    isEmailSubscriber,
    topicNavigation,
    homeTopics,
  })(LandingPage)

  const DecoratedApp = withStaticProps('App', {
    posts,
    topicNavigation,
    state: store.getState(),
  })(App)

  const DecoratedPostList = withStaticProps('PostList', {
    topicNavigation,
    emailUrls,
  })(PostList)

  const DecoratedPost = withStaticProps('PostWrapper', {
    emailUrls,
  })(PostWrapper)

  useEffect(() => {
    const slug = path.split('/').pop() as string

    updateGAMetrics(slug, path)

    scrollToArticleInPathVariable()

    if (searchId) {
      setSearchId(GUIDE_SEARCH_INDEX_KEY, searchId)
    }
  }, [])

  const handleRouteChange = (prevState: any, nextState: any) => {
    if (nextState.location.pathname !== prevState.location.pathname) {
      const {
        params: { slug },
      } = nextState

      updateGAMetrics(slug, nextState.location.pathname)
      // if the mobile nav menu is open, hide it
      PubSub.publish(BLConstants.HIDE_MOBILE_NAV_MODAL_EVENT)
      // Scroll to the top of the page
      window.scrollTo(0, 0)
    }
  }

  const scrollToArticleInPathVariable = () => {
    if (sectionId) {
      scrollTo(sectionId)
    }
  }

  const updateGAMetrics = (slug: string, pathname: string) => {
    if (typeof window === 'undefined') return

    const { ga } = window

    if (!ga) return

    let type = null
    let publishedAtYYYYMM = null

    if (slug) {
      const post = getPostBySlug(slug, posts)
      if (post && post.publishedAt) {
        publishedAtYYYYMM = post.publishedAt.slice(0, 7)
        type = getTopicSlug(post) || post.type
      }
    } else {
      type = pathname.split('/').pop()
    }

    // Content groups must be set before the pageview event is sent.
    // The values persist across router navigation unless explicity 'unset' by setting to null
    ga('set', 'contentGroup1', type) // GA content grouping 'Hello Baby Topics'
    ga('set', 'contentGroup2', publishedAtYYYYMM) // GA content grouping 'Hello Baby Publish Date'
    ga('set', 'contentGroup3', 'Hello Baby') // GA content grouping 'Content Group'
    ga('send', 'pageview', pathname) // send pageview event
  }

  const searchResults = values(searchPosts)

  const isAdmin = currentUser?.admin

  const postRedirects = [
    {
      from: `${blUrls.helloBabyPath}/pottery-barn-kids-baby-holiday-gifts`,
      to: `${blUrls.helloBabyPath}/best-gifts-baby-first-holiday`,
    },
    {
      from: `${blUrls.helloBabyPath}/nursery-design-tips`,
      to: `${blUrls.helloBabyPath}/nursery-organization-hacks`,
    },
    {
      from: `${blUrls.helloBabyPath}/video-the-ergonomic-babybjoern-bouncer-with-thousands-of-5-star-reviews`,
      to: `${blUrls.helloBabyPath}/baby-bjorn-bouncer-balance-soft-video`,
    },
    {
      from: `${blUrls.helloBabyPath}/video-the-ergonomic-babybjorn-bouncer-with-thousands-of-5-star-reviews`,
      to: `${blUrls.helloBabyPath}/baby-bjorn-bouncer-balance-soft-video`,
    },
    {
      from: `${blUrls.helloBabyPath}/anti-racist-starter-kit-babies-toddlers`,
      to: `${blUrls.helloBabyPath}/talking-about-race-toddler`,
    },
  ]

  return (
    <Provider store={store}>
      <GoogleReCaptchaProvider reCaptchaKey={BLConstants.RECAPTCHA_V3_SITE_KEY}>
        <FlagsProvider features={featureFlags}>
          {isAdmin && <AdminTool />}
          <Router history={history}>
            <CompatRouter>
              <Route
                component={DecoratedApp}
                path={blUrls.helloBabyPath}
                onChange={handleRouteChange}
              >
                <IndexRoute component={DecoratedLandingPage} />
                <Route
                  component={(routerProps) => (
                    <PostsDataLoader shouldFetchPosts>
                      <PostList
                        {...routerProps}
                        description={`${searchResults.length} results`}
                        emailUrls={emailUrls}
                        title={`'${routerProps.location.query.search_term}'`}
                        topicNavigation={topicNavigation}
                        searchResults={searchResults}
                      >
                        <PostTeaserList posts={searchResults} />
                      </PostList>
                    </PostsDataLoader>
                  )}
                  path={blUrls.helloBabyPostPath('search')}
                />
                <Route component={DecoratedPostList} path="/">
                  <Route
                    component={() => <TopicView slug={GEAR_SLUG} />}
                    path={blUrls.helloBabyPostPath(GEAR_SLUG)}
                  />
                  <Route
                    component={() => (
                      <TopicView emailUrls={emailUrls} slug={PREGNANCY_SLUG} />
                    )}
                    path={blUrls.helloBabyPostPath(PREGNANCY_SLUG)}
                  />
                  <Route
                    component={() => <TopicView slug={PARENTING_SLUG} />}
                    path={blUrls.helloBabyPostPath(PARENTING_SLUG)}
                  />
                  <Route
                    component={() => (
                      <TopicView slug={SAMPLE_REGISTRIES_SLUG} />
                    )}
                    path={blUrls.helloBabyPostPath(SAMPLE_REGISTRIES_SLUG)}
                  />
                  <Route
                    component={() => (
                      <TopicView slug={PREGNANCY_WEEK_BY_WEEK_SLUG} />
                    )}
                    path={blUrls.helloBabyPostPath(PREGNANCY_WEEK_BY_WEEK_SLUG)}
                  />
                  <Route
                    component={() => (
                      <TopicView slug={FRIENDS_AND_FAMILY_SLUG} />
                    )}
                    path={blUrls.helloBabyPostPath(FRIENDS_AND_FAMILY_SLUG)}
                  />
                  {Object.values(topics).map((topic) => (
                    <Route
                      component={() => <TopicView slug={topic.slug} />}
                      key={topic.slug}
                      path={blUrls.helloBabyPostPath(topic.slug)}
                    />
                  ))}
                </Route>
                {postRedirects.map((redirect) => (
                  <Redirect
                    from={redirect.from}
                    key={redirect.from}
                    to={redirect.to}
                  />
                ))}
                <Route
                  component={DecoratedPost}
                  path={`${blUrls.helloBabyPath}/:slug`}
                />
                {/* used by iOS app for feed guides */}
                <Route
                  component={DecoratedPost}
                  path={`${blUrls.helloBabyPath}/embedded/:slug`}
                />
              </Route>
            </CompatRouter>
          </Router>
        </FlagsProvider>
      </GoogleReCaptchaProvider>
    </Provider>
  )
}

export default withContextualizedTracking({
  eventLocation: track.EventLocation.GUIDE,
})(HelloBabyApp)
